import React, { useMemo } from "react"
import { Link } from "react-router-dom"
import CollapsibleDataTable from "./CollapsibleDataTable"
import { generateStockTitle } from "helpers/stock_helpers"
import { formatReadableDate } from "utils/formatDate"
import { Badge } from "reactstrap"

const MovementKeyList = ({ data = [], loading = false, onPageChange }) => {
  const columns = useMemo(
    () => [
      {
        id: "expander",
        header: "",
        cell: () => null, // handled in table
      },
      {
        header: "Vehicle",
        accessorKey: "stock_id",
        cell: ({ row }) => {
          const title = generateStockTitle(row.original)
          const color = row.original?.exterior_color?.name ?? ""
          const year = row.original?.year?.name ?? ""
          return (
            <div style={{ maxWidth: 150 }} className="text-wrap">
              <div>
                {title || "-"}{" "}
                <Badge className="badge badge-soft-info">{color || ""}</Badge>{" "}
                <Badge className="badge badge-soft-primary">{year || ""}</Badge>
              </div>
            </div>
          )
        },
      },
      {
        header: "Chassis No",
        accessorKey: "chassis",
        cell: ({ row }) => (
          <div>
            <div>{row.original?.full_chassis || "-"}</div>
            <div>
              <Link to={`/stock-list/${row.original?.stock_id || "#"}/view`}>
                {row.original?.stock_id || "-"}
              </Link>
            </div>
          </div>
        ),
      },
      {
        header: "Latest Movement Info",
        accessorKey: "latestMovement",
        cell: ({ row }) => {
          const movements = row.original?.movements || []

          const latest = movements?.[0] || {} // assume API returns latest first
          const from = latest.location_from?.name || "-"
          const to = latest.location_to?.name || "-"
          const movementType = latest.movement_type?.name || "-"
          const movementBy = latest.movement_by?.name || "-"
          const mileage = `${latest?.mileage?.toLocaleString() || 0} km`
          const date = latest?.movement_date
            ? formatReadableDate(latest.movement_date)
            : "-"

          return (
            <div className="d-flex flex-column gap-1">
              <div>
                <strong>{movementType}</strong>
              </div>
              <div className="text-muted">
                <i className="bx bx-calendar me-1 text-secondary"></i>
                {date}
              </div>
              <div className="d-flex align-items-center">
                <i className="bx bx-map-pin me-1 text-danger"></i>
                <span>
                  <strong>From:</strong> {from}
                </span>
              </div>
              <div
                className="d-flex align-items-center badge badge-soft-success"
                style={{ fontSize: 12 }}
              >
                <i className="bx bx-map-pin me-1 text-success"></i>
                <span>
                  <strong>To:</strong> {to}
                </span>
              </div>
              <div className="d-flex align-items-center">
                <i className="bx bx-user me-1 text-info"></i>
                <span>
                  <strong>By:</strong> {movementBy}
                </span>
              </div>
              <div className="d-flex align-items-center">
                <i className="bx bx-tachometer me-1 text-warning"></i>
                <span>
                  <strong>Last Mileage:</strong> {mileage}
                </span>
              </div>
            </div>
          )
        },
      },
      {
        header: "Remarks",
        accessorKey: "remarks",
        cell: ({ row }) => {
          const movements = row.original?.movements || []
          const latest = movements?.[0] || {}
          return latest.remarks || "-"
        },
      },
      {
        header: "Date",
        accessorKey: "created_at",
        cell: ({ row }) => {
          const movements = row.original?.movements || []
          const latest = movements?.[0] || {}
          return latest?.created_at
            ? formatReadableDate(latest?.created_at)
            : "-"
        },
      },
    ],
    []
  )

  const renderSubComponent = ({ row }) => {
    // All movements except the latest for history table
    const childData = row.original?.movements?.slice(1) || []
    if (!childData.length)
      return <div className="text-muted p-2">No previous movements</div>

    return (
      <table className="table table-sm mb-0">
        <thead>
          <tr>
            <th>Date</th>
            <th>Type</th>
            <th>From</th>
            <th>To</th>
            <th>Mileage</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {childData.map((child, i) => (
            <tr key={i}>
              <td>{formatReadableDate(child.movement_date)}</td>
              <td>{child?.movement_type?.name || "-"}</td>
              <td>{child?.location_from?.name || "-"}</td>
              <td>{child?.location_to?.name || "-"}</td>
              <td>{`${child?.mileage?.toLocaleString() || 0} km`}</td>
              <td>{child?.remarks || "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }

  return (
    <CollapsibleDataTable
      columns={columns}
      data={data?.data || []}
      renderSubComponent={renderSubComponent}
      childKey="movements"
      paginationInfo={data}
      loading={loading}
      isPagination={true}
      onPageChange={onPageChange}
    />
  )
}

export default MovementKeyList
